import React, { useEffect, useRef, useState } from 'react';
import { Badge, Spinner } from 'flowbite-react';
import { toast } from 'react-toastify';
import Button from '../../components/Button';
import {
  AssetStatus,
  DraftSummary,
  generateCover,
  getAssetStatus,
} from '../../api/asset-management';

interface Props {
  draft: DraftSummary;
  operatorEmail: string;
}

type RowState = 'idle' | 'generating' | 'ready' | 'error';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_TICKS = 40; // ~2 minutes

const DownloadIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="mr-2 h-4 w-4"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 0 1 1 1v8.59l2.3-2.3a1 1 0 0 1 1.4 1.42l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.42L9 12.6V4a1 1 0 0 1 1-1Zm-6 13a1 1 0 0 1 1-1h10a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1Z"
      clipRule="evenodd"
    />
  </svg>
);

const formatDate = (date?: string) =>
  date ? new Date(date).toLocaleDateString() : '\u2014';

const formatDateTime = (date?: string) => {
  if (!date) return '—';
  const d = new Date(date);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const DraftRow: React.FC<Props> = ({ draft, operatorEmail }) => {
  const initialCover = draft.coverReady;
  const [state, setState] = useState<RowState>(initialCover ? 'ready' : 'idle');
  const [status, setStatus] = useState<AssetStatus | null>(
    initialCover || draft.manuscriptReady
      ? {
          status: 'completed',
          cover_pdf_url: draft.coverPdfUrl,
          manuscript_pdf_url: draft.manuscriptPdfUrl,
          updated_at: new Date().toISOString(),
        }
      : null,
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickCountRef = useRef(0);

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    tickCountRef.current = 0;
  };

  useEffect(() => () => clearPolling(), []);

  const onTick = async () => {
    tickCountRef.current += 1;
    try {
      const next = await getAssetStatus(draft.draftId, operatorEmail);
      setStatus(next);
      if (next.status === 'completed' && next.cover_pdf_url) {
        clearPolling();
        setState('ready');
        return;
      }
      if (next.status === 'failed') {
        clearPolling();
        setState('error');
        toast.error('Generation failed on the cover editor.');
        return;
      }
      if (tickCountRef.current >= MAX_POLL_TICKS) {
        clearPolling();
        setState('error');
        toast.error('Generation timed out after 2 minutes.');
      }
    } catch (err) {
      clearPolling();
      setState('error');
      const axiosErr = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        axiosErr?.response?.data?.message ||
        axiosErr?.message ||
        'Failed to check generation status.';
      toast.error(msg);
    }
  };

  const triggerGeneration = async (forceRegeneration: boolean) => {
    setState('generating');
    setStatus(null);
    try {
      const response = await generateCover(
        draft.draftId,
        { include_spine: true, format: 'pdf', force_regeneration: forceRegeneration },
        operatorEmail,
      );
      if (response.status === 'completed' && response.download_url) {
        setState('ready');
        setStatus({
          status: 'completed',
          cover_pdf_url: response.download_url,
          updated_at: new Date().toISOString(),
        });
        return;
      }
      tickCountRef.current = 0;
      intervalRef.current = setInterval(onTick, POLL_INTERVAL_MS);
    } catch (err) {
      setState('error');
      const axiosErr = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const msg =
        axiosErr?.response?.data?.message ||
        axiosErr?.message ||
        'Failed to start generation.';
      toast.error(msg);
    }
  };

  const coverUrl = status?.cover_pdf_url;
  const manuscriptUrl = status?.manuscript_pdf_url;

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3">
        {draft.thumbnail ? (
          <img
            src={draft.thumbnail}
            alt={draft.draftName}
            className="w-12 h-16 object-cover rounded"
          />
        ) : (
          <div className="w-12 h-16 bg-gray-100 rounded" />
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">{draft.draftName}</td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {formatDate(draft.createdAt)}
      </td>
      <td className="px-4 py-3">
        <Badge color={initialCover || status?.cover_pdf_url ? 'success' : 'gray'}>
          {initialCover || status?.cover_pdf_url ? 'Ready' : 'Pending'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {formatDateTime(status?.cover_generated_at || draft.coverGeneratedAt)}
      </td>
      <td className="px-4 py-3 space-x-2">
        {state === 'idle' && (
          <Button onClick={() => triggerGeneration(false)}>Generate</Button>
        )}
        {state === 'generating' && (
          <span className="inline-flex items-center text-sm text-gray-600">
            <Spinner size="sm" className="mr-2" />
            Generating…
          </span>
        )}
        {state === 'ready' && (
          <div className="inline-flex flex-wrap items-center gap-2">
            {coverUrl && (
              <Button
                onClick={() => window.open(coverUrl, '_blank', 'noreferrer')}
              >
                <DownloadIcon />
                Download Cover
              </Button>
            )}
            {manuscriptUrl && (
              <Button
                variant="LIGHT"
                onClick={() =>
                  window.open(manuscriptUrl, '_blank', 'noreferrer')
                }
              >
                <DownloadIcon />
                Download Manuscript
              </Button>
            )}
            <Button variant="OUTLINED" onClick={() => triggerGeneration(true)}>
              Regenerate
            </Button>
          </div>
        )}
        {state === 'error' && (
          <Button onClick={() => triggerGeneration(false)}>Retry</Button>
        )}
      </td>
    </tr>
  );
};

export default DraftRow;
